/**
 * One call, three ways to say it: the REST request, a curl line, and the MCP
 * `tools/call` that does the same thing.
 *
 * Args are already in the MCP tool's shape (parameters by name, plus
 * `body`), so the MCP twin is the Args as they stand, and REST is them
 * spread over path, query, headers and body. Neither twin ever holds a key:
 * REST and MCP show `sbk_…••••`, curl reads `$SB_KEY`. Anything shaped like a
 * secret (`sbk_`, `sbo_`, `sbv_`, `whsec_`) is scrubbed on the way out, and a
 * `secret` field reads "(shown once)". The user's own Anthropic key (the
 * `anthropicKey` a Director run or a key check carries) reads `sk-ant-…••••`
 * wherever it is, and curl takes it from `$ANTHROPIC_API_KEY`.
 */

import type { Args, Json, Operation, Persona, Twins } from '../contracts';
import { isObj } from '../model/json';


export const SHOWN_ONCE = '(shown once)';
const SECRET = /\b(sbk|sbo|sbv|whsec)_[A-Za-z0-9_-]+/g;
/** An Anthropic API key, anywhere in a text (no word boundary: `_sk-ant-…` is one too). */
export const ANTHROPIC_SECRET = /sk-ant-[A-Za-z0-9_-]+/g;
/** How an Anthropic key is shown, wherever it would have been. */
export const ANTHROPIC_HINT = 'sk-ant-…••••';
/** The body field the user's own Anthropic key rides in (AssistRequest, AssistKeyCheckRequest). */
export const ANTHROPIC_FIELD = 'anthropicKey';
/** The shell variable curl reads the user's Anthropic key from. */
export const ANTHROPIC_VAR = '$ANTHROPIC_API_KEY';

/** A text with every secret-shaped token cut to its prefix. */
export const redactText = (s: string): string =>
  s.replace(SECRET, (_m, prefix: string) => `${prefix}_…••••`).replace(ANTHROPIC_SECRET, ANTHROPIC_HINT);

/** A JSON value with secrets scrubbed; a `secret` field is replaced outright, and so is an `anthropicKey`, whatever it holds. */
export const redact = (v: Json): Json => {
  if (typeof v === 'string') return redactText(v);
  if (Array.isArray(v)) return v.map(redact);
  if (isObj(v)) {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k,
      k === 'secret' && typeof x === 'string' ? SHOWN_ONCE : k === ANTHROPIC_FIELD && typeof x === 'string' ? ANTHROPIC_HINT : redact(x)]));
  }
  return v;
};

/** How a key is shown: its prefix only. */
export const keyHint = (key: string): string => `${key.includes('_') ? key.slice(0, key.indexOf('_')) : 'key'}_…••••`;

/** The shell variable curl reads a key from. */
export const keyVar = (persona: Persona, operator: boolean): string =>
  operator ? '$SB_OPERATOR_KEY' : persona === 'A' ? '$SB_KEY' : `$SB_KEY_${persona}`;

export interface RestRequest {
  readonly method: string;
  /** Base + path + query. */
  readonly url: string;
  /** Path + query, without the base. */
  readonly target: string;
  /** Everything but authorization. */
  readonly headers: Readonly<Record<string, string>>;
  readonly body?: Json;
}

const text = (v: Json): string => (Array.isArray(v) ? v.map((x) => String(x)).join(',') : typeof v === 'string' ? v : JSON.stringify(v));

/** The HTTP request Args stand for, without the key. Unbound path holes stay as `{name}`. */
export const restOf = (op: Operation, args: Args, apiBase: string): RestRequest => {
  let path = op.path;
  const query = new URLSearchParams();
  const headers: Record<string, string> = {};
  const seen = new Set<string>();
  for (const h of op.holes) {
    if (h.in === 'body' || seen.has(`${h.in}:${h.name}`)) continue;
    seen.add(`${h.in}:${h.name}`);
    const value = args[h.name];
    if (value === undefined || value === null) continue;
    // A lone surrogate would make encodeURIComponent throw; it goes as U+FFFD and the API refuses it.
    if (h.in === 'path') path = path.replace(`{${h.name}}`, encodeURIComponent(text(value).toWellFormed()));
    else if (h.in === 'query') query.set(h.name, text(value));
    else headers[h.name] = text(value);
  }
  const body = op.method === 'GET' ? undefined : args.body;
  if (body !== undefined) headers['content-type'] = 'application/json';
  const search = query.toString();
  const target = `${path}${search ? `?${search}` : ''}`;
  return { method: op.method, url: `${apiBase}${target}`, target, headers, ...(body === undefined ? {} : { body }) };
};

/** The JSON-RPC message that makes the same call through `POST /v1/mcp`. */
export const mcpOf = (op: Operation, args: Args, id: number | string): Json =>
  ({ jsonrpc: '2.0', id, method: 'tools/call', params: { name: op.id, arguments: args as Json } });

const quote = (s: string): string => `'${s.replace(/'/g, `'\\''`)}'`;

/** Where the user's Anthropic key stood in a quoted body: the quote closes, the shell reads the variable, the quote reopens. */
const FROM_SHELL = `"${ANTHROPIC_FIELD}":"${ANTHROPIC_HINT}"`;

/** A curl line: `$SB_API` for the base, the key as a shell variable, JSON in single quotes; an Anthropic key is `$ANTHROPIC_API_KEY`. */
export const curlOf = (method: string, target: string, headers: Readonly<Record<string, string>>, body: Json | undefined, keyVariable: string | null): string => {
  const lines = [`curl -sS${method === 'GET' ? '' : ` -X ${method}`} "$SB_API${redactText(target)}"`];
  if (keyVariable) lines.push(`-H "authorization: Bearer ${keyVariable}"`);
  for (const [k, v] of Object.entries(headers)) lines.push(`-H ${quote(`${k}: ${redactText(v)}`)}`);
  if (body !== undefined) lines.push(`-d ${quote(JSON.stringify(redact(body))).replace(FROM_SHELL, () => `"${ANTHROPIC_FIELD}":"'"${ANTHROPIC_VAR}"'"`)}`);
  return lines.join(' \\\n  ');
};

export interface TwinOptions {
  readonly apiBase: string;
  /** The key the call goes with; only its prefix is shown. */
  readonly key: string | null;
  readonly persona?: Persona;
  /** The JSON-RPC id of the MCP twin. */
  readonly id?: number | string;
}

/** REST, curl and MCP for one call. MCP is null for an operation marked `x-mcp: false`. */
export const twinsOf = (op: Operation, args: Args, o: TwinOptions): Twins => {
  const rest = restOf(op, args, o.apiBase);
  const keyed = op.security === 'key' || op.security === 'operator';
  const headers: Record<string, string> = {};
  if (keyed && o.key) headers.authorization = `Bearer ${keyHint(o.key)}`;
  for (const [k, v] of Object.entries(rest.headers)) headers[k] = redactText(v);
  return {
    rest: { method: rest.method, url: redactText(rest.url), headers, ...(rest.body === undefined ? {} : { body: redact(rest.body) }) },
    curl: curlOf(rest.method, rest.target, rest.headers, rest.body, keyed ? keyVar(o.persona ?? 'A', op.security === 'operator') : null),
    mcp: op.mcp ? redact(mcpOf(op, args, o.id ?? 1)) : null,
  };
};
